<template>
  <fts-btn 
    :tag="tag"
    :text="text" 
    :outline="outline" 
    :color="color" 
    :rippleColor="rippleColor"
    :xs="xs"
    :sm="sm"
    :lg="lg"
    :textTransformInit="textTransformInit"
    :boxShadowNone="boxShadowNone"
    :disabled="disabled" 
    :loading="loading" 
    :emoji="emoji" 
    :href="href"
    ref="ftsBtn" 
    class="fts-btn-hold"
    @click.native.prevent>
    <slot /> 
    <div class="hold-overlay" :style="{'width': loadingBar}" v-if="loadingBar">
      <font-awesome-icon :icon="['fas', 'angle-left']" class="bounce-left"/>
      <font-awesome-icon :icon="['fas', 'angle-right']" class="bounce-right"/>  
    </div>
  </fts-btn>
</template>

<script>
import FTSBtn from "./FTSBtn";

export default {
  props: {
    tag: String,
    href: String,
    /* Style */ 
    text: Boolean,
    outline: Boolean,
    color: String,
    rippleColor: String,
    xs: Boolean,
    sm: Boolean,
    lg: Boolean,
    textTransformInit: Boolean,
    boxShadowNone: Boolean,
    /* Function */
    disabled: Boolean,
    loading: Boolean,
    /* Others */
    emoji: String
  },
  components: {
    "fts-btn": FTSBtn,
  },
  data: () => ({
    timer: 0.6,
    longPressEvent: false,
    interval: false,
    count: 0,
    escape: false
  }),
  methods: {
    resetInterval() {
      clearInterval(this.interval)
      this.interval = false
    },
    start(e) {
      if (e.type === 'mousedown' && e.button !== 0) 
        return

      if (!this.interval) {
        this.interval = setInterval(() => {
          if (this.count < this.timer) {
            this.count += 0.05
          } else {
            this.longPressEvent = true
            this.$emit('longPressEvent')
            this.resetInterval()
          }
        }, 50)
      }
    },
    cancel() {
      // Execute
      if (this.escape) {
        // Reset Escape
        this.escape = false
      } else if (!this.longPressEvent) {
        this.$emit('clickEvent')
      }
      // Reset Long Press Event
      if (this.longPressEvent)
        this.longPressEvent = false
      // Reset Interval
      if (this.interval)
        this.resetInterval()
      // Reset Count
      this.count = 0
    }
  },
  computed: {
    loadingBar() {
      if (this.count)
        return `${(this.count / this.timer).toFixed(2) * 100}%` 
    }
  },
  mounted() {
    // Override MouseOutEvent
    let vm = this
    function onMouseOut(event) {
      var e = event.toElement || event.relatedTarget;
      if (e.parentNode == this || e == this) 
        return;
      // Escape
      vm.escape = true
      vm.cancel()
    }   
    ;['mousedown', 'touchstart'].forEach(e => this.$refs.ftsBtn.$el.addEventListener(e, this.start))
    ;['click', 'touchend', 'touchcancel'].forEach(e => this.$refs.ftsBtn.$el.addEventListener(e, this.cancel))
    this.$refs.ftsBtn.$el.addEventListener('mouseout', onMouseOut, true)
  }
}
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

@mixin initial() {
  $buttons: (
    'primary': fts-primary, 
    'secondary': fts-secondary,
    'success': fts-success,
    'danger': fts-danger,
    'warning': fts-warning,
    'dark': fts-dark,
    'ternary-green': fts-ternary-green,
    'ternary-blue': fts-ternary-blue,
    'default': fts-default
  );

  @each $name, $color in $buttons {
    &.fts-btn-#{$name} {
      .hold-overlay {
        @include theme() {
          color: theme-get($color);
        }
      }
    }
  }
}

.fts-btn-hold {
  @include initial();
  &.btn-custom {
    .hold-overlay {
      color: currentColor;
    }
  }
  .hold-overlay {
    @include theme {
      background-color: rgba(theme-get('fts-gray-25'), 0.5);
    }
    position: absolute;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 0.25rem;
    padding-right: 0.25rem; 
    overflow: hidden;
    white-space: nowrap; 
    color: var(--fts-default);
    pointer-events: none;
    > svg {
      pointer-events: none;
    }
  }
  // bounce
  $bounceLeft40: 8px;
  $bounceLeft60: 4px;
  $bounceRight40: -8px;
  $bounceRight60: -4px;
  // left bounce
  @-webkit-keyframes bounceLeft {
    0%,
    20%,
    50%,
    80%,
    100% {
      -webkit-transform: translateX(0);
      transform: translateX(0);
    }
    40% {
      -webkit-transform: translateX($bounceLeft40);
      transform: translateX($bounceLeft40);
    }
    60% {
      -webkit-transform: translateX($bounceLeft60);
      transform: translateX($bounceLeft60);
    }
  }
  @-moz-keyframes bounceLeft {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateX(0);
    }
    40% {
      transform: translateX($bounceLeft40);
    }
    60% {
      transform: translateX($bounceLeft60);
    }
  }
  @keyframes bounceLeft {
    0%,
    20%,
    50%,
    80%,
    100% {
      -ms-transform: translateX(0);
      transform: translateX(0);
    }
    40% {
      -ms-transform: translateX($bounceLeft40);
      transform: translateX($bounceLeft40);
    }
    60% {
      -ms-transform: translateX($bounceLeft60);
      transform: translateX($bounceLeft60);
    }
  }
  // right bounce
  @-webkit-keyframes bounceRight {
    0%,
    20%,
    50%,
    80%,
    100% {
      -webkit-transform: translateX(0);
      transform: translateX(0);
    }
    40% {
      -webkit-transform: translateX($bounceRight40);
      transform: translateX($bounceRight40);
    }
    60% {
      -webkit-transform: translateX($bounceRight60);
      transform: translateX($bounceRight60);
    }
  }
  @-moz-keyframes bounceRight {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateX(0);
    }
    40% {
      transform: translateX($bounceRight40);
    }
    60% {
      transform: translateX($bounceRight60);
    }
  }
  @keyframes bounceRight {
    0%,
    20%,
    50%,
    80%,
    100% {
      -ms-transform: translateX(0);
      transform: translateX(0);
    }
    40% {
      -ms-transform: translateX($bounceRight40);
      transform: translateX($bounceRight40);
    }
    60% {
      -ms-transform: translateX($bounceRight60);
      transform: translateX($bounceRight60);
    }
  }
  .bounce-left {
    -webkit-animation: bounceLeft 2s infinite;
    animation: bounceLeft 2s infinite;
  }
  .bounce-right {
    -webkit-animation: bounceRight 2s infinite;
    animation: bounceRight 2s infinite;
  }
}
</style>